import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, FormError, SubmitButton } from "@/components/common";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

const NONE = "__none";

export function MechanicSelect({ value, onChange, testId = "job-mechanic-select" }) {
  const { data } = useFetch("/booking/settings");
  return (
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>{t("jobs.unassigned")}</SelectItem>
        {(data?.staff || []).map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function NewJobDialog({ open, onOpenChange, appointment, onCreated }) {
  const { isManager } = useGarage();
  const [f, setF] = useState({ customer_id: "", vehicle_id: "", reason: "", odometer_in: "", promised_at: "", mechanic_user_id: appointment?.mechanic_user_id || null, received: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const customers = useFetch("/customers");
  const vehicles = useFetch("/vehicles", { customer_id: f.customer_id || "none" });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = { reason: f.reason, received: f.received, odometer_in: f.odometer_in === "" ? null : Number(f.odometer_in), promised_at: f.promised_at || null,
        ...(isManager ? { mechanic_user_id: f.mechanic_user_id } : {}),
        ...(appointment ? { appointment_id: appointment.id } : { customer_id: f.customer_id, vehicle_id: f.vehicle_id }) };
      const { data } = await api.post("/jobs", body);
      toast.success(t("jobs.created", { number: data.number }));
      onCreated(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="new-job-dialog">
        <DialogHeader><DialogTitle>{appointment ? t("jobs.receiveVehicle") : t("jobs.new")}</DialogTitle>
          <DialogDescription>{appointment ? `${appointment.customer_name} · ${appointment.vehicle}` : t("jobs.newHint")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormError message={error} />
          {!appointment && <>
            <Field label={t("sched.customer")}>
              <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v, vehicle_id: "" })}>
                <SelectTrigger data-testid="job-customer-select"><SelectValue placeholder={t("sched.chooseCustomer")} /></SelectTrigger>
                <SelectContent>{(customers.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{`${c.first_name} ${c.last_name}`.trim()}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {f.customer_id && <Field label={t("sched.vehicle")}>
              <Select value={f.vehicle_id} onValueChange={(v) => setF({ ...f, vehicle_id: v })}>
                <SelectTrigger data-testid="job-vehicle-select"><SelectValue placeholder={t("sched.chooseVehicle")} /></SelectTrigger>
                <SelectContent>{(vehicles.data || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {v.make} {v.model}</SelectItem>)}</SelectContent>
              </Select>
            </Field>}
          </>}
          <Field label={t("jobs.reason")} hint={appointment ? t("jobs.reasonFromBooking") : ""}><Textarea value={f.reason} onChange={set("reason")} data-testid="job-reason-input" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("jobs.odometer")}><Input type="number" min={0} value={f.odometer_in} onChange={set("odometer_in")} data-testid="job-odometer-input" /></Field>
            <Field label={t("jobs.promised")}><Input type="date" value={f.promised_at} onChange={set("promised_at")} data-testid="job-promised-input" /></Field>
          </div>
          {isManager && <Field label={t("sched.mechanic")}><MechanicSelect value={f.mechanic_user_id} onChange={(v) => setF({ ...f, mechanic_user_id: v })} /></Field>}
          {!appointment && <label className="flex items-center gap-3 text-sm"><Switch checked={f.received} onCheckedChange={(v) => setF({ ...f, received: v })} data-testid="job-received-switch" /> {t("jobs.receivedNow")}</label>}
          <div className="gf-modal-footer">
          <SubmitButton busy={busy} className="w-full" disabled={!appointment && !f.vehicle_id} data-testid="job-create-submit">{t("jobs.create")}</SubmitButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
